/**
 * Save Pack for All Realms (docs/PARITY-STEP-4-PLAN.md decision 7): on the desktop a pack is
 * kept in your own settings; on the web, in this browser's private storage for the site
 * (OPFS), as the zip Export Pack writes. Any realm on this device can then add it with Add
 * Saved Pack. Nothing is sent anywhere; another device or browser has its own.
 */

const FOLDER = 'saved-icon-packs';

async function folder(create: boolean): Promise<FileSystemDirectoryHandle | null> {
  if (!navigator.storage?.getDirectory) return null;
  try {
    return await (await navigator.storage.getDirectory()).getDirectoryHandle(FOLDER, { create });
  } catch {
    return null;
  }
}

/** A pack's file name: its name, made safe, as a zip. */
const fileOf = (pack: string) => `${pack.replace(/[^\p{L}\p{N} _-]/gu, '').trim().slice(0, 100) || 'pack'}.zip`;

export async function savePack(pack: string, zip: Uint8Array): Promise<boolean> {
  const dir = await folder(true);
  if (!dir) return false;
  try {
    const handle = await dir.getFileHandle(fileOf(pack), { create: true });
    const writable = await handle.createWritable();
    await writable.write(zip as Uint8Array<ArrayBuffer>);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

/** The saved packs' names, by name. */
export async function savedPacks(): Promise<string[]> {
  const dir = await folder(false);
  if (!dir) return [];
  const names: string[] = [];
  try {
    for await (const [name, handle] of (dir as unknown as { entries(): AsyncIterable<[string, FileSystemHandle]> }).entries()) {
      if (handle.kind === 'file' && name.endsWith('.zip')) names.push(name.slice(0, -4));
    }
  } catch {
    return [];
  }
  return names.sort((a, b) => a.localeCompare(b));
}

export async function readSavedPack(pack: string): Promise<Uint8Array | null> {
  const dir = await folder(false);
  if (!dir) return null;
  try {
    return new Uint8Array(await (await (await dir.getFileHandle(fileOf(pack))).getFile()).arrayBuffer());
  } catch {
    return null;
  }
}

export async function forgetSavedPack(pack: string): Promise<void> {
  const dir = await folder(false);
  try {
    await dir?.removeEntry(fileOf(pack));
  } catch {
    // Already gone.
  }
}
